import type React from 'react';
import { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { navigate } from 'vike/client/router';

import AppBackButton from '@/components/AppBackButton';
import type { WithFormErrors } from '@/components/Forms/FormControl';
import useFormSubmit from '@/components/Forms/useFormSubmit';
import srcHeroPattern from '@/pages/(marketing)/index/components/images/hero-pattern.svg';
import useMe from '@/renderer/utils/useMe';
import { usePageContext } from '@/renderer/utils/usePageContext';
import { useRequest } from '@/renderer/utils/useRequest';
import { useRewardful } from '@/renderer/utils/useRewardful';
import type { RequestSchema } from '@/types/request';

import { MixpanelProxy, mixpanelTrack } from '@/renderer/utils/stat/mixp';
import type { OAuthQueryParams } from '../oauth/authorize/+Page';
import ProgressSteps from './components/ProgressSteps';
import Stats from './components/Stats';
import StepOne from './components/StepOne';
import StepTwo from './components/StepTwo';
import Testimonials from './components/Testimonials';
import type { StepOneData } from './components/types';

// Types
type Step = 1 | 2;

// API requests
const authRegisterRequest: RequestSchema<
  { message: string; username: string },
  StepOneData & { referral?: string; v2Verify?: true }
> = {
  name: 'authRegisterRequest',
  input: '/api/v1/auth/register',
  method: 'POST',
};

const authLoginRequest: RequestSchema<
  { message: string; session: API.SessionToken; user: API.User },
  { username: string; password: string }
> = {
  name: 'authLoginRequest',
  input: '/api/v1/auth/login',
  method: 'POST',
};

// Main component
const RegistrationForm: React.FC = () => {
  // Redirect URI
  const { urlParsed } = usePageContext();
  const queryParams = urlParsed.search as OAuthQueryParams | undefined;

  // State
  const [step, setStep] = useState<Step>(1);
  const [_stepOneData, setStepOneData] = useState<StepOneData | null>(null);

  const defaultEmail = urlParsed?.search?.email || '';

  // Form hooks
  const stepOneForm = useForm<WithFormErrors<StepOneData>>({
    defaultValues: { email: defaultEmail || '' },
  });
  // Request hooks
  const authRegister = useRequest(authRegisterRequest, { manual: true, hideAlert: true });
  const authLogin = useRequest(authLoginRequest, { manual: true, hideAlert: true });

  const [_user, setUser] = useMe(true);

  const affiliate = useRewardful();

  const redirectUri = useMemo(() => {
    if (queryParams) {
      const {
        client_id: clientId,
        scope,
        state,
        nonce,
        redirect_uri: redirectUriBase,
        display,
        prompt,
        error,
        error_description: errorDescription,
        secure,
      } = queryParams;

      // if (
      //   redirect &&
      //   redirect.match(/^(https?:\/\/(.*\.)?(raspicks.com|bettingtalk\.com))?\/.*$/)
      // ) {
      //   return redirect;
      // }

      if (clientId) {
        const search = {
          client_id: clientId,
          scope: scope || '',
          state: state || '',
          nonce: nonce || '',
          redirect_uri: redirectUriBase || '',
          display: display || '',
          prompt: prompt || '',
          error: error || '',
          error_description: errorDescription || '',
          secure: secure || '',
        };

        const params = new URLSearchParams(
          Object.entries(search)
            .filter(([, value]) => value !== '')
            .map(([key, value]) => [key, value]),
        );

        return `/oauth/authorize?${params.toString()}`;
      }
    }

    return '/dashboard';
  }, [
    queryParams?.client_id,
    queryParams?.scope,
    queryParams?.state,
    queryParams?.nonce,
    queryParams?.redirect_uri,
    queryParams?.display,
    queryParams?.prompt,
    queryParams?.error,
    queryParams?.error_description,
    queryParams?.secure,
  ]);

  const handleStepOneSubmit = useFormSubmit(
    {
      form: stepOneForm,
      callback: async (body) => {
        if (body.website) {
          return;
        }
        delete body.website;
        const result = await authRegister.run(
          {
            ...body,
            username: body.email.replace(/[^a-zA-Z0-9]/g, ''),
            v2Verify: true,
            referral: affiliate
              ? affiliate
              : /^[A-Za-z0-9-]{4,36}$/.test(sessionStorage?.referral)
                ? sessionStorage?.referral
                : '',
          },
          { throwOnRun: true },
        );

        if (result?.message === 'API.AUTHENTICATION.REGISTER.SUCCESS') {
          // Now log in
          const data = await authLogin.run(
            { username: result.username, password: body.password },
            { throwOnRun: true },
          );

          if (data?.user) {
            setUser(data.user, data.session.refresh);

            try {
              // Identify user in Mixpanel
              MixpanelProxy.identify(data.user.id.toString());
              mixpanelTrack('auth/register', {
                userId: data.user.id,
                email: data.user.email,
                firstName: data.user.firstName,
                lastName: data.user.lastName,
              });
            } catch (error) {
              console.warn('Failed to identify user in Mixpanel:', error);
            }

            setStepOneData(body);
            setStep(2);
          }
        }
      },
    },
    [authRegister, authLogin],
  );

  // Render
  return (
    <div className="relative flex min-h-full flex-1">
      {/* The app has no browser chrome, so give the native register screen a way
          back to the landing / sign-in (#2039). Renders nothing on web. */}
      <AppBackButton />
      <div className="flex h-auto flex-1 justify-center">
        <div className="my-auto flex min-h-fit flex-col px-4 py-12 sm:px-6 lg:flex-none lg:px-20 xl:px-24">
          <div className="mx-auto w-full max-w-sm space-y-10 lg:w-96">
            <ProgressSteps step={step} />

            {step === 1 && (
              <StepOne
                form={stepOneForm}
                onSubmit={handleStepOneSubmit}
                request={authRegister}
                defaultEmail={defaultEmail}
              />
            )}

            {step === 2 && (
              <StepTwo
                onSkip={() => {
                  if (redirectUri.startsWith('/')) {
                    navigate(redirectUri);
                  } else {
                    location.href = redirectUri;
                  }
                }}
                onSuccess={() => {
                  if (redirectUri.startsWith('/')) {
                    navigate(redirectUri);
                  } else {
                    location.href = redirectUri;
                  }
                }}
              />
            )}
          </div>
        </div>
      </div>
      <div
        className="hidden h-fit min-h-full flex-1 flex-col bg-linear-to-bl bg-zinc-950 from-emerald-400/50 to-70% bg-repeat lg:flex"
        style={{ backgroundImage: `url("${encodeURI(decodeURIComponent(srcHeroPattern))}")` }}
      >
        <div className="flex flex-1 flex-col justify-start bg-linear-to-bl from-emerald-400/50 to-70%">
          <div className="my-8 flex flex-1 flex-col justify-center px-4 text-center lg:px-8 ">
            <div>
              <p className="mb-8 text-left font-semibold text-white text-xl">
                Trusted and endorsed by...
              </p>
              <Testimonials />
            </div>
          </div>
          <Stats />
        </div>
      </div>
    </div>
  );
};

export default RegistrationForm;
